/**
 * Field formatters and display labels for the Financial Analysis
 * change-history feed.
 *
 * Used by: `FinancialPeriodDetailPanel.tsx` -> `<ChangeHistory />`.
 */

const fmtCurrency = (v: number): string =>
  v.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
  });

const fmtRatio = (v: number): string => v.toFixed(2);

const CURRENCY_FIELDS = new Set<string>([
  "totalRevenue",
  "costOfGoodsSold",
  "grossProfit",
  "operatingExpenses",
  "operatingProfit",
  "netProfitBeforeTax",
  "incomeTax",
  "netProfitAfterTax",
  "cashAndEquivalents",
  "tradeReceivables",
  "inventory",
  "otherCurrentAssets",
  "totalCurrentAssets",
  "propertyPlantEquipment",
  "intangibleAssets",
  "otherNonCurrentAssets",
  "totalNonCurrentAssets",
  "totalAssets",
  "tradePayables",
  "shortTermBorrowings",
  "currentPortionLongTermDebt",
  "otherCurrentLiabilities",
  "totalCurrentLiabilities",
  "longTermBorrowings",
  "otherNonCurrentLiabilities",
  "totalNonCurrentLiabilities",
  "totalLiabilities",
  "netAssets",
  "retainedEarnings",
  "totalEquity",
  "operatingCashFlow",
  "investingCashFlow",
  "financingCashFlow",
  "netCashFlow",
  "taxLiabilities",
  "superannuationLiabilities",
  "relatedPartyReceivables",
  "relatedPartyPayables",
  "payablesCurrent",
  "payables30Days",
  "payables60Days",
  "payables90PlusDays",
  "receivablesCurrent",
  "receivables30Days",
  "receivables60Days",
  "receivables90PlusDays",
  "workingCapital",
]);

const RATIO_FIELDS = new Set<string>(["currentRatio", "debtRatio"]);

function buildFormatters(): Record<string, (v: unknown) => string> {
  const out: Record<string, (v: unknown) => string> = {};
  for (const field of CURRENCY_FIELDS) {
    out[field] = (v) =>
      typeof v === "number" ? fmtCurrency(v) : String(v ?? "\u2014");
  }
  for (const field of RATIO_FIELDS) {
    out[field] = (v) =>
      typeof v === "number" ? fmtRatio(v) : String(v ?? "\u2014");
  }
  return out;
}

export const FINANCIAL_PERIOD_FIELD_FORMATTERS = buildFormatters();

export const FINANCIAL_PERIOD_FIELD_LABELS: Record<string, string> = {
  totalRevenue: "Total revenue",
  costOfGoodsSold: "Cost of goods sold",
  grossProfit: "Gross profit",
  operatingExpenses: "Operating expenses",
  operatingProfit: "Operating profit",
  netProfitBeforeTax: "Net profit before tax",
  incomeTax: "Income tax",
  netProfitAfterTax: "Net profit after tax",
  cashAndEquivalents: "Cash & equivalents",
  tradeReceivables: "Trade receivables",
  inventory: "Inventory",
  otherCurrentAssets: "Other current assets",
  totalCurrentAssets: "Total current assets",
  propertyPlantEquipment: "Property, plant & equipment",
  intangibleAssets: "Intangible assets",
  otherNonCurrentAssets: "Other non-current assets",
  totalNonCurrentAssets: "Total non-current assets",
  totalAssets: "Total assets",
  tradePayables: "Trade payables",
  shortTermBorrowings: "Short-term borrowings",
  currentPortionLongTermDebt: "Current portion of long-term debt",
  otherCurrentLiabilities: "Other current liabilities",
  totalCurrentLiabilities: "Total current liabilities",
  longTermBorrowings: "Long-term borrowings",
  otherNonCurrentLiabilities: "Other non-current liabilities",
  totalNonCurrentLiabilities: "Total non-current liabilities",
  totalLiabilities: "Total liabilities",
  netAssets: "Net assets",
  retainedEarnings: "Retained earnings",
  totalEquity: "Total equity",
  operatingCashFlow: "Operating cash flow",
  investingCashFlow: "Investing cash flow",
  financingCashFlow: "Financing cash flow",
  netCashFlow: "Net cash flow",
  taxLiabilities: "Tax liabilities",
  superannuationLiabilities: "Superannuation liabilities",
  relatedPartyReceivables: "Related-party receivables",
  relatedPartyPayables: "Related-party payables",
  payablesCurrent: "Payables current",
  payables30Days: "Payables 30 days",
  payables60Days: "Payables 60 days",
  payables90PlusDays: "Payables 90+ days",
  receivablesCurrent: "Receivables current",
  receivables30Days: "Receivables 30 days",
  receivables60Days: "Receivables 60 days",
  receivables90PlusDays: "Receivables 90+ days",
  currentRatio: "Current ratio",
  debtRatio: "Debt ratio",
  workingCapital: "Working capital",
};
